import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { EventModel, EventModelUtils, EventSubStatus } from '@app/events/shared/event.model';
import { DatePipe, NgClass } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { Calendar, CalendarOff, Copy, FileDown, LucideAngularModule, UserX, X } from 'lucide-angular';
import { RemindersComponent } from '@app/events/view/reminders.component';
import { NotesComponent } from '@app/events/view/notes.component';
import { ClientAccessPanelComponent } from '@app/events/view/client-access-panel.component';
import { ItineraryComponent } from '@app/events/view/itinerary.component';
import { FinancialSummaryComponent } from '@app/events/view/financial-summary.component';
import { EventPlannerComponent } from '@app/events/view/planner.component';
import { PlannerSuppliersComponent } from '@app/events/view/planner-suppliers.component';
import { EventGuestsComponent } from '@app/events/view/guests.component';
import { RulesConductComponent } from '@app/events/view/rules-conduct.component';
import { AccommodationComponent } from '@app/events/view/accommodation.component';
import { TablePlanComponent } from '@app/table-plan/table-plan.component';
import { hasRoomOutline } from '@app/table-plan/canvas/room-outlines/rooms';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { EventService, PencilConflictPayload, PencilDraftSibling } from '@app/events/shared/event.service';
import { AuthService } from '@app/auth/auth.service';
import { UserModel } from '@app/users/shared/user.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@app/shared/toast/toast.service';
import { EventEditComponent } from '@app/events/edit/event-edit.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Editor, NgxEditorModule, toDoc, toHTML, Toolbar } from 'ngx-editor';
import { debounceTime } from 'rxjs';
import { ComponentCanDeactivate } from '@app/shared/guards/unsaved-changes.guard';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { MarkAsLostModalComponent, MarkAsLostPayload } from '@app/events/view/mark-as-lost-modal.component';
import { EventPdfBuilderModalComponent } from '@app/events/view/event-pdf-builder-modal.component';

type EventViewTab =
  | 'event-details'
  | 'itinerary'
  | 'financial-summary'
  | 'guests'
  | 'table-plan'
  | 'accommodation'
  | 'planner'
  | 'suppliers'
  | 'rules-conduct'
  | 'notes'
  | 'reminders';

@Component({
  selector: 'app-event-view',
  imports: [
    BadgeComponent,
    DatePipe,
    ButtonComponent,
    LucideAngularModule,
    NgClass,
    RemindersComponent,
    NotesComponent,
    ClientAccessPanelComponent,
    ItineraryComponent,
    FinancialSummaryComponent,
    EventPlannerComponent,
    PlannerSuppliersComponent,
    EventGuestsComponent,
    RulesConductComponent,
    AccommodationComponent,
    TablePlanComponent,
    RouterLink,
    EventEditComponent,
    NgxEditorModule,
    ReactiveFormsModule,
    MarkAsLostModalComponent,
    EventPdfBuilderModalComponent,
  ],
  templateUrl: './event-view.component.html',
  styleUrls: ['./event-view.component.css'],
})
export class EventViewComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
  protected event: EventModel;
  protected timelineItem: EventTimelineItemModel;
  protected isGeneratingPdf = false;
  // TVG-52: controls the "Create PDF" section picker modal.
  protected pdfBuilderOpen = false;
  protected itineraryReloadToken = 0;
  protected itineraryScrollToken = 0;
  protected pendingTimelineScrollId?: string;
  protected plannerHasUnsavedChanges = false;

  protected tab: EventViewTab = 'event-details';

  // TVG-142: when a guest tile on the Table Plan canvas is clicked, jump to the
  // Guests tab and open that guest's record. Cleared once the modal has opened.
  protected guestToOpenId: number | null = null;

  protected readonly FileDown = FileDown;
  // TVG-120: Clone Event action icon.
  protected readonly CopyIcon = Copy;

  // TVG-121: Cancel Viewing / Did Not Attend action icons.
  protected readonly XIcon = X;
  protected readonly UserXIcon = UserX;

  // TVG-122: Move to Pencil / Release Date action icons.
  protected readonly CalendarIcon = Calendar;
  protected readonly CalendarOffIcon = CalendarOff;

  protected readonly EventModelUtils = EventModelUtils;

  // TVG-120: tracks in-flight clone request so the button can be disabled.
  protected isCloning = false;

  /**
   * TVG-187: canonical event tab order and labels confirmed by Richard. The
   * admin/staff view shares the same menu as the customer portal, so it uses
   * the same order and labels — Notes and Reminders are staff-only and sit at
   * the end (positions 11 and 12 in the spec). This list is the single source
   * of truth for both the desktop and mobile tab bars. Visibility (accommodation
   * selection and Table Plan toggle) is applied in visibleTabOptions and keeps
   * the existing staff-side gating.
   */
  protected readonly tabOptions: { value: EventViewTab; label: string }[] = [
    { value: 'event-details', label: 'Details' },
    { value: 'financial-summary', label: 'Products & Finances' },
    { value: 'itinerary', label: 'Itinerary' },
    { value: 'planner', label: 'Planner' },
    { value: 'suppliers', label: 'Suppliers' },
    { value: 'guests', label: 'Guests' },
    { value: 'table-plan', label: 'Table Plan' },
    { value: 'accommodation', label: 'Bedrooms' },
    { value: 'rules-conduct', label: 'Rules' },
    { value: 'notes', label: 'Notes' },
    { value: 'reminders', label: 'Reminders' },
  ];

  protected get visibleTabOptions(): { value: EventViewTab; label: string }[] {
    return this.tabOptions.filter((option) => {
      if (option.value === 'accommodation') {
        return !!this.event?.has_accommodation_tab;
      }
      if (option.value === 'table-plan') {
        return this.tablePlanEnabled && this.venueHasTablePlanRoom;
      }
      return true;
    });
  }

  protected get tablePlanEnabled(): boolean {
    return this.featureToggleService.isEnabled('table-plan-tab');
  }

  /**
   * TVG-372: only a venue with a drawn room gets a Table Plan. Red House Barn has
   * none yet, so its events show no Table Plan tab instead of the Milling Barn room.
   */
  protected get venueHasTablePlanRoom(): boolean {
    return hasRoomOutline(this.event?.venue?.name);
  }

  // TVG-104: the printable Table Plan pack button is gated on its own toggle so
  // the option is unavailable until an admin enables it (default off on prod).
  protected get tablePlanPdfEnabled(): boolean {
    return this.featureToggleService.isEnabled('table-plan-pdf');
  }

  protected get crmSalesFlowEnabled(): boolean {
    return this.featureToggleService.isEnabled('crm-sales-flow');
  }

  // TVG-120: Clone Event button visibility. Gated on the CRM Sales Flow toggle
  // so toggle-off hides the action entirely.
  protected get cloneEventEnabled(): boolean {
    return this.featureToggleService.isEnabled('crm-sales-flow');
  }

  /**
   * TVG-352: cloning raises a new event, so the button carries the same
   * admin-or-manager gate as Create Event (EventCloneController authorizes
   * against EventPolicy::create). Kept separate from `cloneEventEnabled`,
   * which also gates the viewing actions below.
   */
  protected get canCloneEvent(): boolean {
    return this.cloneEventEnabled && this.isAdminOrManager;
  }

  /**
   * TVG-296: a Confirmed booking is past the sales pipeline, so none of the
   * pipeline status actions belong on it any more. Every one of them would
   * demote a signed booking back down the funnel, which is never a deliberate
   * thing to do from this page.
   */
  protected get isConfirmedBooking(): boolean {
    return this.event?.status === 'Confirmed';
  }

  // TVG-121: Cancel Viewing / No-Show. Toggle-gated; the buttons only show
  // when a viewing is booked (cancel) or once the viewing date has passed
  // (no-show). Both endpoints clear show_round and the card demotes back to
  // Active Opportunity on the Kanban.
  //
  // TVG-296: hidden once the booking is Confirmed — the viewing led to the
  // booking, so cancelling it or marking a no-show is meaningless there.
  protected viewingActionInFlight = false;

  protected get canCancelViewing(): boolean {
    return this.cloneEventEnabled && !!this.event?.show_round && !this.isConfirmedBooking;
  }

  protected get canMarkNoShow(): boolean {
    if (!this.cloneEventEnabled || !this.event?.show_round || this.isConfirmedBooking) {
      return false;
    }
    const showRound = this.event.show_round;
    const ts = typeof showRound === 'string' ? Date.parse(showRound) : new Date(showRound).getTime();
    if (Number.isNaN(ts)) {
      return false;
    }
    return ts <= Date.now();
  }

  protected async cancelViewing(): Promise<void> {
    if (!this.event?.id || this.viewingActionInFlight) {
      return;
    }
    const proceed = await this.confirmationModal.confirm({
      title: 'Cancel viewing',
      message: 'Cancel the booked viewing for this event?',
      confirmLabel: 'Cancel viewing',
      cancelLabel: 'Keep viewing',
      confirmButtonStyle: 'danger',
    });
    if (!proceed) {
      return;
    }
    this.viewingActionInFlight = true;
    this.eventService.cancelViewing(this.event.id).subscribe({
      next: (response) => {
        this.viewingActionInFlight = false;
        if (response?.data) {
          this.event = { ...this.event, ...response.data };
        } else {
          this.event = { ...this.event, show_round: null };
        }
        this.toastService.success('Viewing cancelled');
      },
      error: () => {
        this.viewingActionInFlight = false;
        this.toastService.error('Unable to cancel viewing');
      },
    });
  }

  protected async markNoShow(): Promise<void> {
    if (!this.event?.id || this.viewingActionInFlight) {
      return;
    }
    const proceed = await this.confirmationModal.confirm({
      title: 'Mark did not attend',
      message: 'Record that the prospect did not attend? The card will move back to Active Opportunity.',
      confirmLabel: 'Mark did not attend',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'danger',
    });
    if (!proceed) {
      return;
    }
    this.viewingActionInFlight = true;
    this.eventService.markViewingNoShow(this.event.id).subscribe({
      next: (response) => {
        this.viewingActionInFlight = false;
        if (response?.data) {
          this.event = { ...this.event, ...response.data };
        } else {
          this.event = { ...this.event, show_round: null };
        }
        this.toastService.success('Marked as did not attend');
      },
      error: () => {
        this.viewingActionInFlight = false;
        this.toastService.error('Unable to record no-show');
      },
    });
  }

  protected async cloneEvent(): Promise<void> {
    if (!this.event?.id || this.isCloning) {
      return;
    }
    const proceed = await this.confirmationModal.confirm({
      title: 'Clone event',
      message: 'Create a new sibling event from this one? The clone starts as an Opportunity with a fresh status and references this event as its parent.',
      confirmLabel: 'Clone Event',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'primary',
    });
    if (!proceed) {
      return;
    }

    this.isCloning = true;
    this.eventService.cloneEvent(this.event.id).subscribe({
      next: (response) => {
        this.isCloning = false;
        const cloneId = response.data?.id;
        if (cloneId) {
          this.toastService.success('Clone created');
          this.router.navigate(['/events', cloneId]);
        } else {
          this.toastService.error('Clone created but no ID was returned');
        }
      },
      error: () => {
        this.isCloning = false;
        this.toastService.error('Unable to clone event');
      },
    });
  }

  // TVG-119: Surface the Active Opportunity sub-status toggle only while the
  // event is in the Opportunity bucket and the CRM Sales Flow toggle is on.
  protected get subStatusToggleVisible(): boolean {
    return this.crmSalesFlowEnabled && this.event?.status === 'Opportunity';
  }

  protected isSubStatusActive(value: EventSubStatus): boolean {
    return this.event?.sub_status === value;
  }

  protected setSubStatus(value: EventSubStatus): void {
    if (!this.event?.id || this.isSubStatusActive(value) || this.subStatusUpdating) {
      return;
    }

    this.subStatusUpdating = true;
    this.eventService.updateSubStatus(this.event.id, value).subscribe({
      next: (response) => {
        if (response.data) {
          this.event = { ...this.event, ...response.data };
        } else {
          this.event = { ...this.event, sub_status: value };
        }
        this.subStatusUpdating = false;
      },
      error: () => {
        this.subStatusUpdating = false;
        this.toastService.error('Unable to update sub-status');
      },
    });
  }

  protected subStatusUpdating = false;

  // TVG-124: Mark as Lost modal state. Surfaced only when the CRM Sales Flow
  // toggle is on and the event is currently in a status from which Lost is a
  // legal next state (Active Opportunity, Viewing Booked, Pencil — which all
  // map to status ∈ {Opportunity, Provisional} in the data model).
  protected markAsLostOpen = false;
  protected markAsLostSubmitting = false;

  protected get markAsLostVisible(): boolean {
    return (
      this.crmSalesFlowEnabled &&
      (this.event?.status === 'Opportunity' || this.event?.status === 'Provisional')
    );
  }

  // TVG-123 / TVG-296: the "Cancel booking" action has been taken off the
  // booking record. It sat next to the other status buttons and read as an
  // ordinary cancel while actually flipping the event to Cancelled, which is
  // not a decision to make from a stray click on the event page. The
  // POST /events/{event}/cancel-booking endpoint and EventService.cancelBooking
  // are untouched, so the action can be reinstated behind a deliberate flow.

  protected openMarkAsLost(): void {
    if (!this.markAsLostVisible || this.markAsLostSubmitting) {
      return;
    }
    this.markAsLostOpen = true;
  }

  protected closeMarkAsLost(): void {
    this.markAsLostOpen = false;
  }

  protected submitMarkAsLost(payload: MarkAsLostPayload): void {
    if (!this.event?.id || this.markAsLostSubmitting) {
      return;
    }

    this.markAsLostSubmitting = true;
    this.eventService.markAsLost(this.event.id, payload).subscribe({
      next: (response) => {
        if (response.data) {
          this.event = { ...this.event, ...response.data };
        } else {
          this.event = { ...this.event, status: 'Lost' };
        }
        this.markAsLostSubmitting = false;
        this.markAsLostOpen = false;
        this.toastService.success('Event marked as Lost');
      },
      error: () => {
        this.markAsLostSubmitting = false;
        this.toastService.error('Unable to mark event as Lost');
      },
    });
  }

  // TVG-122: Move to Pencil + Release Date. Both gated on crm-sales-flow plus
  // a role/per-event SM check. The Move button shows from Opportunity (and
  // hides at Provisional/Confirmed/etc); Release shows only at Provisional.
  // TVG-124 ships the Lost exit alongside Release on the Pencil card.
  protected pencilActionInFlight = false;
  private currentUser: UserModel | null = null;

  private get currentUserRoles(): string[] {
    return this.currentUser?.roles ?? [];
  }

  private get isAdminOrManager(): boolean {
    return this.currentUserRoles.includes('admin') || this.currentUserRoles.includes('manager');
  }

  // TVG-43: Client Access panel visibility. Staff-only (admin|manager|staff)
  // plus the crm-sales-flow toggle, mirroring the server-side gating. Never
  // visible to customers.
  protected get canViewClientAccess(): boolean {
    if (!this.crmSalesFlowEnabled) {
      return false;
    }
    return this.currentUserRoles.includes('admin')
      || this.currentUserRoles.includes('manager')
      || this.currentUserRoles.includes('staff')
      || this.currentUserRoles.includes('salesperson');
  }

  private get isAssignedSalesManager(): boolean {
    return !!this.event?.sales_manager_id
      && this.currentUser?.id === this.event.sales_manager_id;
  }

  protected get canMoveToPencil(): boolean {
    if (!this.crmSalesFlowEnabled || !this.event) {
      return false;
    }
    if (
      this.event.status === 'Provisional' ||
      this.event.status === 'Cancelled' ||
      this.event.status === 'Closed' ||
      this.event.status === 'Lost' ||
      // TVG-296: a Confirmed booking must not offer a demotion back to Pencil.
      this.event.status === 'Confirmed'
    ) {
      return false;
    }
    return this.isAdminOrManager || this.isAssignedSalesManager;
  }

  protected get canReleasePencil(): boolean {
    if (!this.crmSalesFlowEnabled || this.event?.status !== 'Provisional') {
      return false;
    }
    return this.isAdminOrManager || this.isAssignedSalesManager;
  }

  protected async moveToPencil(): Promise<void> {
    if (!this.event?.id || this.pencilActionInFlight) {
      return;
    }
    const proceed = await this.confirmationModal.confirm({
      title: 'Move to Provisional',
      message: 'Hold this date provisionally for the prospect? The card will move to the Provisional column and the date will be blocked.',
      confirmLabel: 'Move to Provisional',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'primary',
    });
    if (!proceed) {
      return;
    }
    this.runPencilFlip(false);
  }

  private runPencilFlip(acknowledgeConcurrentPencil: boolean): void {
    if (!this.event?.id) {
      return;
    }
    this.pencilActionInFlight = true;
    this.eventService.moveToPencil(this.event.id, acknowledgeConcurrentPencil).subscribe({
      next: (response) => {
        this.pencilActionInFlight = false;
        if (response.data) {
          this.event = { ...this.event, ...response.data };
        }
        this.toastService.success('Moved to Provisional');
        if (response.other_drafts?.length) {
          this.surfaceClonesPrompt(response.other_drafts);
        }
      },
      error: async (error: HttpErrorResponse) => {
        this.pencilActionInFlight = false;
        if (error.status === 409 && error.error?.conflict) {
          await this.handlePencilConflict(error.error.conflict as PencilConflictPayload);
          return;
        }
        this.toastService.error('Unable to move event to Provisional');
      },
    });
  }

  private async handlePencilConflict(conflict: PencilConflictPayload): Promise<void> {
    const eventRef = conflict.event_reference ?? ('#' + conflict.event_id);
    const proceed = await this.confirmationModal.confirm({
      title: 'Already has a Provisional event',
      message: `${conflict.contact_name} already has Provisional event ${eventRef} — promote anyway?`,
      confirmLabel: 'Promote anyway',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'danger',
    });
    if (proceed) {
      this.runPencilFlip(true);
    }
  }

  private async surfaceClonesPrompt(otherDrafts: PencilDraftSibling[]): Promise<void> {
    const summary = otherDrafts
      .map((draft) => '• ' + (draft.reference ?? draft.name ?? ('#' + draft.id)) + ' (' + draft.status + ')')
      .join('\n');
    const count = otherDrafts.length;
    const label = count === 1 ? 'other draft event' : 'other draft events';
    await this.confirmationModal.alert({
      title: 'Other draft events on this contact',
      message: `${count} ${label} remain open:\n\n${summary}\n\nThey will keep an "alternative to Provisional event" flag on the contact page until you act on them.`,
      confirmLabel: 'Got it',
    });
  }

  protected async releasePencil(): Promise<void> {
    if (!this.event?.id || this.pencilActionInFlight) {
      return;
    }
    const proceed = await this.confirmationModal.confirm({
      title: 'Release date',
      message: 'Release the held date and return the event to Active Opportunity? The date will be unblocked.',
      confirmLabel: 'Release date',
      cancelLabel: 'Keep Provisional',
      confirmButtonStyle: 'danger',
    });
    if (!proceed) {
      return;
    }
    this.pencilActionInFlight = true;
    this.eventService.releasePencil(this.event.id).subscribe({
      next: (response) => {
        this.pencilActionInFlight = false;
        if (response.data) {
          this.event = { ...this.event, ...response.data };
        }
        this.toastService.success('Date released');
      },
      error: () => {
        this.pencilActionInFlight = false;
        this.toastService.error('Unable to release date');
      },
    });
  }

  protected allergyForm = new FormGroup({
    has_guest_allergies: new FormControl(false),
    guest_allergies: new FormControl<any>(''),
  });
  protected allergyEditor?: Editor;
  protected allergyToolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo'],
  ];

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private eventService: EventService,
    private toastService: ToastService,
    private confirmationModal: ConfirmationModalService,
    private featureToggleService: FeatureToggleService,
    private authService: AuthService,
  ) {
    this.event = this.route.snapshot.data['event'];
    this.timelineItem = this.route.snapshot.data['timelineItem'];

    // TVG-207: honour a `?tab=` query param so other pages (e.g. the payment
    // remediation list) can deep-link straight to a specific tab such as
    // Products & Finances, rather than always landing on Event details.
    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    if (requestedTab && this.tabOptions.some((option) => option.value === requestedTab)) {
      this.tab = requestedTab as EventViewTab;
    }

    if (this.timelineItem) {
      this.timelineNavigate(this.timelineItem.id + '');
    }

    this.initializeAllergyForm();

    // TVG-122: Move-to-Pencil / Release Date visibility depends on the
    // current user's role plus the per-event Sales Manager assignment.
    this.authService.getUser().subscribe({
      next: (user) => {
        this.currentUser = user;
      },
      error: () => {
        this.currentUser = null;
      },
    });
  }

  protected timelineNavigate(itemId: string) {
    this.setTab('itinerary');
    this.pendingTimelineScrollId = itemId;
    this.itineraryScrollToken++;
  }

  protected async selectTab(value: EventViewTab): Promise<void> {
    if (value === this.tab) {
      return;
    }

    const shouldDiscardChanges = await this.confirmDiscardPlannerChanges();

    if (!shouldDiscardChanges) {
      return;
    }

    this.setTab(value);
  }

  // TVG-142: open a guest's record from the Table Plan canvas. Switch to the
  // Guests tab and hand the id down so the guest modal opens there.
  protected async onOpenGuestRecord(guestId: number): Promise<void> {
    this.guestToOpenId = guestId;
    await this.selectTab('guests');
  }

  protected onGuestRecordOpened(): void {
    this.guestToOpenId = null;
  }

  protected onPlannerDirtyStateChange(isDirty: boolean): void {
    this.plannerHasUnsavedChanges = isDirty;
  }

  protected onSupplierDirtyStateChange(isDirty: boolean): void {
    this.plannerHasUnsavedChanges = isDirty;
  }

  protected reloadEvent(): void {
    // VG-413: Reload event data to refresh itinerary when products are updated
    if (this.event?.id) {
      this.eventService.getOverview(this.event.id.toString()).subscribe({
        next: (response) => {
          if (response.data) {
            this.event = {
              ...this.event,
              ...response.data,
            };
            this.initializeAllergyForm();
            this.itineraryReloadToken++;
          }
        },
        error: (error) => {
          console.error('Error reloading event:', error);
        }
      });
    }
  }

  protected onEventUpdated(updatedEvent: EventModel): void {
    this.event = updatedEvent;
    this.initializeAllergyForm();
    this.reloadEvent();
  }

  // TVG-52: open/close the "Create PDF" section picker.
  protected openPdfBuilder(): void {
    if (this.isGeneratingPdf) {
      return;
    }
    this.pdfBuilderOpen = true;
  }

  protected closePdfBuilder(): void {
    this.pdfBuilderOpen = false;
  }

  // TVG-52: generate a PDF containing only the ticked sections. Selecting all
  // sections reproduces the previous full event PDF.
  protected generateSectionedPdf(sections: string[]): void {
    if (this.isGeneratingPdf || !this.event?.id || !sections.length) {
      return;
    }

    this.pdfBuilderOpen = false;
    this.isGeneratingPdf = true;

    this.eventService.downloadPdf(this.event.id, sections).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const safeName = this.event.name ? this.event.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') : 'event';
        const link = document.createElement('a');
        link.href = url;
        link.download = `${safeName || 'event'}-details.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isGeneratingPdf = false;
      },
      error: () => {
        this.isGeneratingPdf = false;
        this.toastService.error('Unable to generate PDF');
      },
    });
  }

  protected downloadKitchenPdf(): void {
    if (this.isGeneratingPdf || !this.event?.id) {
      return;
    }

    this.isGeneratingPdf = true;

    this.eventService.downloadKitchenPdf(this.event.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const safeName = this.event.name ? this.event.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') : 'event';
        const link = document.createElement('a');
        link.href = url;
        link.download = `${safeName || 'event'}-kitchen-chef.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isGeneratingPdf = false;
      },
      error: () => {
        this.isGeneratingPdf = false;
        this.toastService.error('Unable to generate kitchen PDF');
      },
    });
  }

  // TVG-104: download the printable Table Plan pack (short header, landscape
  // room plan, guest seat list, per-table pages). Mirrors the Kitchen PDF flow.
  protected downloadTablePlanPdf(): void {
    if (this.isGeneratingPdf || !this.event?.id) {
      return;
    }

    this.isGeneratingPdf = true;

    this.eventService.downloadTablePlanPdf(this.event.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const safeName = this.event.name ? this.event.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') : 'event';
        const link = document.createElement('a');
        link.href = url;
        link.download = `${safeName || 'event'}-table-plan.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isGeneratingPdf = false;
      },
      error: () => {
        this.isGeneratingPdf = false;
        this.toastService.error('Unable to generate Table Plan PDF');
      },
    });
  }

  protected cleanText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text;
  }

  ngOnInit(): void {
    this.allergyEditor = new Editor({
      history: true,
      keyboardShortcuts: true,
    });

    this.allergyForm.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.saveAllergies();
      });
  }

  ngOnDestroy(): void {
    this.allergyEditor?.destroy();
  }

  async canDeactivate(): Promise<boolean> {
    return this.confirmDiscardPlannerChanges();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (!this.plannerHasUnsavedChanges) {
      return;
    }

    $event.preventDefault();
    $event.returnValue = true;
  }

  protected onRulesConductUpdated(updatedEvent: EventModel): void {
    this.event = updatedEvent;
  }

  private initializeAllergyForm(): void {
    this.allergyForm.patchValue(
      {
        has_guest_allergies: true,
        guest_allergies: this.event?.guest_allergies ? toDoc(this.event.guest_allergies) : '',
      },
      { emitEvent: false },
    );
  }

  private async confirmDiscardPlannerChanges(): Promise<boolean> {
    if (!this.plannerHasUnsavedChanges) {
      return true;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Unsaved changes',
      message: 'You have unsaved changes in the Event Planner or Suppliers. If you leave now, those changes will be lost.',
      confirmLabel: 'Leave Without Saving',
      cancelLabel: 'Stay Here',
      confirmButtonStyle: 'danger',
    });

    if (confirmed) {
      this.plannerHasUnsavedChanges = false;
    }

    return confirmed;
  }

  private setTab(value: EventViewTab): void {
    this.tab = value;
  }

  private saveAllergies(): void {
    if (!this.event?.id) {
      return;
    }

    const allergiesValue = this.allergyForm.get('guest_allergies')?.value;
    let allergiesContent = '';

    if (allergiesValue && typeof allergiesValue === 'object') {
      allergiesContent = toHTML(allergiesValue);
    } else {
      allergiesContent = allergiesValue || '';
    }

    this.eventService.updateAllergies(this.event.id, {
      has_guest_allergies: true,
      guest_allergies: allergiesContent,
    }).subscribe({
      next: (response) => {
        if (response.data) {
          this.event = response.data;
        }
      },
      error: (error) => {
        console.error('Error saving allergies:', error);
        this.toastService.error('Failed to save guest allergies');
      },
    });
  }
}
